import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddSongStep } from 'fym-shared/src/music24/catalogAddSong';
import { AddSongFrom, catalogAddSongPath, parseAddSongFrom, parseAddSongStep } from './catalogAddSongPath';

export interface AddSongRoute {
  /** The catalog row once step 1 saved it. */
  itemUuid: string | undefined;
  /** The step of the address; undefined when the address names none. */
  requestedStep: AddSongStep | undefined;
  from: AddSongFrom;
  /** Opens a step. `replace` keeps the browser history short (the save of step 1). */
  goTo: (step: AddSongStep, options?: { itemUuid?: string; replace?: boolean }) => void;
}

/**
 * The address of the "Add a song" page (design catalog-add-song a·why: a URL per step). Reload,
 * Back and "Finish setup" open the right step of the saved song.
 */
export function useAddSongRoute(context: 'catalog' | 'onboarding'): AddSongRoute {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const itemUuid = params.get('item') ?? undefined;
  const requestedStep = parseAddSongStep(params.get('step'));
  const from: AddSongFrom = context === 'onboarding' ? 'onboarding' : parseAddSongFrom(params.get('from'));

  const goTo = useCallback(
    (step: AddSongStep, options: { itemUuid?: string; replace?: boolean } = {}) => {
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(catalogAddSongPath({ item: options.itemUuid ?? itemUuid, step, from }), {
        replace: options.replace === true,
      });
    },
    [from, itemUuid, navigate]
  );

  return useMemo(() => ({ itemUuid, requestedStep, from, goTo }), [from, goTo, itemUuid, requestedStep]);
}
